import React, { useState, useEffect, useCallback } from 'react';
import { cleaningAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Host status board for the cleaning turnover dispatch: upcoming/active jobs and
// where each one stands in the Accept/Decline/backup cascade. Reads
// GET /api/cleaning/jobs (turnover tasks with their dispatch state).

const STATUS_PILL = {
  offering: { label: 'Waiting on cleaner', cls: 'bg-amber-100 text-amber-800' },
  confirmed: { label: 'Confirmed', cls: 'bg-emerald-100 text-emerald-800' },
  exhausted: { label: 'Needs you', cls: 'bg-red-100 text-red-800' },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-100 text-slate-600' },
};

const OFFER_MARK = {
  pending: { icon: '•', cls: 'text-amber-600', text: 'waiting' },
  accepted: { icon: '✓', cls: 'text-emerald-600', text: 'accepted' },
  declined: { icon: '✕', cls: 'text-slate-400', text: 'declined' },
  expired: { icon: '⌛', cls: 'text-slate-400', text: 'no answer' },
  skipped: { icon: '–', cls: 'text-slate-400', text: 'no email' },
};

const CleaningTurnovers = () => {
  const { getAuthToken } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      const data = await cleaningAPI.listJobs(token);
      setJobs(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Could not load your turnovers.');
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  useEffect(() => { load(); }, [load]);

  const jobLine = (job) => {
    if (job.status === 'completed') {
      const n = (job.completionPhotos || []).length;
      return `Done by ${job.assignedCleanerName || 'your cleaner'}${n ? ` · ${n} photo(s)` : ''}`;
    }
    if (job.dispatchStatus === 'confirmed') return `${job.assignedCleanerName || 'Cleaner'} is booked in`;
    if (job.dispatchStatus === 'exhausted') return 'No cleaner accepted — arrange this one yourself';
    if (job.dispatchStatus === 'offering') {
      const cur = (job.dispatchOffers || []).find((o) => o.cleanerId === job.currentOfferCleanerId);
      return cur ? `Offered to ${cur.name}, waiting for a reply` : 'Finding a cleaner…';
    }
    return '';
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-[#1F3A68]">Cleaning turnovers</h3>
          <p className="text-xs text-slate-500">
            Automated turnover cleans and where each stands in the accept / backup cascade.
          </p>
        </div>
        <button onClick={load} className="text-xs text-[#1F3A68] font-semibold hover:underline">
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-slate-500 text-sm py-4">Loading…</p>
      ) : error ? (
        <p className="text-red-600 text-sm py-4">{error}</p>
      ) : jobs.length === 0 ? (
        <p className="text-slate-500 text-sm py-4">
          No turnover cleans yet. Turn on "Auto-offer cleaning on external turnovers" for a property,
          or book a one-off clean, and jobs will appear here.
        </p>
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => {
            const pill = job.status === 'completed'
              ? { label: 'Done', cls: 'bg-emerald-100 text-emerald-800' }
              : (STATUS_PILL[job.dispatchStatus] || { label: job.dispatchStatus, cls: 'bg-slate-100 text-slate-600' });
            return (
              <div key={job._id} className="border border-slate-100 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{job.propertyTitle}</p>
                    <p className="text-xs text-slate-500">
                      Checkout {job.checkoutDate}
                      {job.platform && job.platform !== 'Manual' ? ` · ${job.platform}` : ''}
                      {job.source === 'manual_turnover' ? ' · one-off' : ''}
                    </p>
                  </div>
                  <span className={`shrink-0 text-[11px] font-semibold px-2 py-1 rounded-full ${pill.cls}`}>
                    {pill.label}
                  </span>
                </div>

                <p className="text-sm text-slate-600 mt-2">{jobLine(job)}</p>

                {(job.dispatchOffers || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    {job.dispatchOffers.map((o, i) => {
                      const m = OFFER_MARK[o.status] || OFFER_MARK.pending;
                      return (
                        <span key={i} className="text-[11px] text-slate-500">
                          <span className={m.cls}>{m.icon}</span> {o.name}
                          <span className="text-slate-400"> ({o.role === 'preferred' ? 'preferred' : 'backup'} · {m.text})</span>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CleaningTurnovers;
